import { Edge, Node, XYPosition } from "@xyflow/react";
import { getInteractionFromFactory } from "../../features/interaction_factory/InteractionFactory";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { copyInteractionWithLocalizationFallback } from "../../helpers/InteractionCopyHelper";
import { IAnswer } from "../Quiz";
import Interaction, { InteractionType } from "./Interaction";
import { ITrigger, TriggerType, getTriggerLabel } from "./Trigger";

export type SequenceNodeData = {
  name: string;
  id: string;
  interaction: Interaction;
  parentSequenceId?: string;
  color?: string;
};

export type SequenceCommentZone = {
  id: string;
  position: XYPosition;
  size: {
    width: number;
    height: number;
  };
  label: string;
  color?: string;
};

export type SequenceTextbox = {
  id: string;
  position: XYPosition;
  size: {
    width: number;
    height: number;
  };
  text: string;
  color?: string;
  selected?: boolean;
};

export interface ISequence extends Interaction {
  edges: Edge[];
  nodes: Node<SequenceNodeData>[];
  commentZones?: SequenceCommentZone[];
  textboxes?: SequenceTextbox[];
  finishAll: boolean;
  abortOthersOnFinish: boolean;
}

export const TAKE3D_SEQUENCE_ROOT_NODE_ID = "n1";
export const TAKE3D_ON_START_NODE_ID = "take3d-onStart";
export const TAKE3D_ON_END_NODE_ID = "take3d-onEnd";
export const TAKE3D_DEFAULT_TRIGGER_TYPES = [
  TriggerType.OnStart,
  TriggerType.OnEnd,
] as const;

function createRootSequenceNode(): Node<SequenceNodeData> {
  return {
    id: TAKE3D_SEQUENCE_ROOT_NODE_ID,
    position: { x: 0, y: 0 },
    data: {
      name: "Sequence",
      id: TAKE3D_SEQUENCE_ROOT_NODE_ID,
      interaction: {
        id: GetNewGuid(),
        name: "Sequence",
        type: InteractionType.Sequence,
        finishAll: true,
        abortOthersOnFinish: false,
      } as ISequence,
    },
    type: "sequence",
  };
}

function createTriggerNode(
  nodeId: string,
  triggerType: TriggerType,
  position: XYPosition,
): Node<SequenceNodeData> {
  return {
    id: nodeId,
    position,
    data: {
      name: getTriggerLabel(triggerType),
      id: nodeId,
      interaction: {
        id: GetNewGuid(),
        name: getTriggerLabel(triggerType),
        type: InteractionType.Trigger,
        triggerType,
      } as ITrigger,
    },
    type: "sequence",
  };
}

export const createDefaultSequenceInteraction = (): ISequence => ({
  id: GetNewGuid(),
  name: "Sequence",
  type: InteractionType.Sequence,
  edges: [],
  nodes: [createRootSequenceNode()],
  commentZones: [],
  textboxes: [],
  finishAll: true,
  abortOthersOnFinish: false,
});

export const createDefaultTake3DSequenceInteraction = (): ISequence => ({
  id: GetNewGuid(),
  name: "Sequence",
  type: InteractionType.Sequence,
  edges: [],
  nodes: [
    createRootSequenceNode(),
    createTriggerNode(TAKE3D_ON_START_NODE_ID, TriggerType.OnStart, {
      x: 0,
      y: 200,
    }),
    createTriggerNode(TAKE3D_ON_END_NODE_ID, TriggerType.OnEnd, { x: 0, y: 400 }),
  ],
  commentZones: [],
  textboxes: [],
  finishAll: true,
  abortOthersOnFinish: false,
});

export function ensureTake3DSequenceDefaults(sequence?: ISequence): ISequence {
  const baseSequence = sequence
    ? {
        ...sequence,
        nodes: sequence.nodes ? [...sequence.nodes] : [],
        edges: sequence.edges ? [...sequence.edges] : [],
        commentZones: sequence.commentZones ? [...sequence.commentZones] : [],
        textboxes: sequence.textboxes ? [...sequence.textboxes] : [],
      }
    : createDefaultTake3DSequenceInteraction();

  if (!baseSequence.nodes || baseSequence.nodes.length === 0) {
    return createDefaultTake3DSequenceInteraction();
  }

  const nodes = [...baseSequence.nodes];
  const hasRootNode = nodes.some(
    (node) => node.data?.interaction?.type === InteractionType.Sequence,
  );

  if (!hasRootNode) {
    nodes.unshift(createRootSequenceNode());
  }

  const hasOnStart = nodes.some(
    (node) =>
      node.data?.interaction?.type === InteractionType.Trigger &&
      (node.data.interaction as ITrigger).triggerType === TriggerType.OnStart,
  );
  const hasOnEnd = nodes.some(
    (node) =>
      node.data?.interaction?.type === InteractionType.Trigger &&
      (node.data.interaction as ITrigger).triggerType === TriggerType.OnEnd,
  );

  if (!hasOnStart) {
    nodes.push(
      createTriggerNode(TAKE3D_ON_START_NODE_ID, TriggerType.OnStart, {
        x: 0,
        y: 200,
      }),
    );
  }

  if (!hasOnEnd) {
    nodes.push(
      createTriggerNode(TAKE3D_ON_END_NODE_ID, TriggerType.OnEnd, {
        x: 0,
        y: 400,
      }),
    );
  }

  return {
    ...baseSequence,
    nodes,
    edges: baseSequence.edges ?? [],
    commentZones: baseSequence.commentZones ?? [],
    textboxes: baseSequence.textboxes ?? [],
    finishAll: baseSequence.finishAll ?? true,
    abortOthersOnFinish: baseSequence.abortOthersOnFinish ?? false,
  };
}

export const copySequenceInteraction = (
  interaction: Interaction,
  getKeys: (amount: number) => string[],
  replaceKey: (from: string, to: string) => void,
  getAnswerArray: (
    originalAnswers: IAnswer[],
    newKeys: string[],
  ) => IAnswer[],
): ISequence => {
  const original = interaction as ISequence;
  const originalNodes = original.nodes ?? [];
  const nodeIdMap = new Map<string, string>(
    originalNodes.map((node) => [node.id, GetNewGuid()]),
  );
  const safeAnswerMapper =
    getAnswerArray ?? ((originalAnswers: IAnswer[]) => originalAnswers);

  const copiedNodes = originalNodes.map((node) => {
    const newNodeId = nodeIdMap.get(node.id)!;
    const originalInteraction = node.data?.interaction;
    let copiedInteraction = originalInteraction;

    if (originalInteraction) {
      const factoryEntity = getInteractionFromFactory(originalInteraction.type);
      if (factoryEntity?.Copy) {
        copiedInteraction = factoryEntity.Copy(
          originalInteraction,
          getKeys,
          replaceKey,
          safeAnswerMapper,
        );
      } else {
        copiedInteraction = copyInteractionWithLocalizationFallback(
          originalInteraction,
          getKeys,
          replaceKey,
          GetNewGuid,
        );
      }
    }

    return {
      ...node,
      id: newNodeId,
      position: { ...node.position } as XYPosition,
      data: node.data
        ? {
            ...node.data,
            id: newNodeId,
            interaction: copiedInteraction ?? node.data.interaction,
          }
        : node.data,
    };
  });

  const copiedEdges = (original.edges ?? []).map((edge) => ({
    ...edge,
    id: GetNewGuid(),
    source: nodeIdMap.get(edge.source) ?? edge.source,
    target: nodeIdMap.get(edge.target) ?? edge.target,
  }));

  const copiedCommentZones = (original.commentZones ?? []).map((zone) => ({
    ...zone,
    id: GetNewGuid(),
    position: { ...zone.position },
    size: { ...zone.size },
  }));

  const copiedTextboxes = (original.textboxes ?? []).map((textbox) => ({
    ...textbox,
    id: GetNewGuid(),
    position: { ...textbox.position },
    size: { ...textbox.size },
  }));

  return {
    ...original,
    id: GetNewGuid(),
    name: original.name ?? "Sequence",
    edges: copiedEdges,
    nodes: copiedNodes,
    commentZones: copiedCommentZones,
    textboxes: copiedTextboxes,
    finishAll: original.finishAll ?? true,
    abortOthersOnFinish: original.abortOthersOnFinish ?? false,
  };
};
